import { Link, useParams, Navigate } from "react-router-dom";
import DoospLayout from "@/components/doosp/DoospLayout";
import { getProgram, PROGRAMS } from "@/lib/programs";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight, ShieldAlert, ShieldCheck, HeartPulse, Flame, Biohazard, CheckCircle2,
  Clock, Users, Award, BookOpen, Sparkles, Building2, Target, FileCheck2, GraduationCap, ChevronRight,
} from "lucide-react";

const ICONS = { ShieldAlert, ShieldCheck, HeartPulse, Flame, Biohazard };

export default function ProgramDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const program = getProgram(slug);

  if (!program) return <Navigate to="/programs" replace />;

  const Icon = ICONS[program.icon] || ShieldCheck;
  const isLive = program.status === "live";
  const ctaHref = isLive ? (user ? "/dashboard" : "/register") : "/contact";

  const related = PROGRAMS.filter((p) => p.slug !== program.slug).slice(0, 3);

  return (
    <DoospLayout>
      {/* HERO */}
      <section className="relative border-b border-zinc-200 overflow-hidden">
        <div className={`absolute inset-0 bg-${program.color}-950/5 pointer-events-none`} />
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Link to="/programs" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-red-800">
                ← All programs
              </Link>
              <div className="flex items-center gap-3 mt-4">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-lg bg-${program.color}-50`}>
                  <Icon className={`h-7 w-7 text-${program.color}-600`} />
                </div>
                {isLive ? (
                  <span className="text-[10px] font-mono uppercase tracking-widest bg-red-600 text-white px-2.5 py-1 rounded">Live · Available now</span>
                ) : (
                  <span className="text-[10px] font-mono uppercase tracking-widest bg-zinc-100 text-zinc-500 px-2.5 py-1 rounded">Coming soon</span>
                )}
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mt-6" data-testid={`program-heading-${program.slug}`}>
                {program.name}
              </h1>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-3">{program.tagline}</p>
              <p className="text-lg text-zinc-700 mt-6 leading-relaxed max-w-2xl">{program.summary}</p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                {isLive ? (
                  <Link to={ctaHref} className="bg-red-600 hover:bg-red-700 text-white h-12 px-7 inline-flex items-center gap-2 rounded-md text-base font-medium shadow-lg shadow-red-600/20" data-testid={`program-cta-${program.slug}`}>
                    Start certification <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <Link to={ctaHref} className="bg-red-600 hover:bg-red-700 text-white h-12 px-7 inline-flex items-center gap-2 rounded-md text-base font-medium shadow-lg shadow-red-600/20" data-testid={`program-waitlist-${program.slug}`}>
                    Join the waitlist <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
                <Link to="/corporate" className="border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white h-12 px-6 inline-flex items-center gap-2 rounded-md text-sm font-medium transition">
                  <Building2 className="h-4 w-4" /> Certify a team
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border-2 border-zinc-200 shadow-2xl">
                <img src={program.heroImage} alt={program.name} className="w-full h-80 md:h-[420px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KEY STATS */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: BookOpen, label: "Modules", value: program.modules },
            { icon: Clock, label: "Duration", value: program.durationLabel },
            { icon: Award, label: "Validity", value: "2 years" },
            { icon: Users, label: "Price", value: `$${program.priceUsd} USD` },
          ].map((s) => (
            <div key={s.label} className="border-l-4 border-red-600 pl-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
              <div className="font-display text-2xl font-black mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT YOU'LL LEARN */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-16">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-800">What you&rsquo;ll learn</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 leading-tight">
              Curriculum highlights
            </h2>
            <ul className="mt-8 space-y-4">
              {program.highlights.map((h, i) => (
                <li key={`hl-${i}-${String(h).slice(0, 20)}`} className="flex items-start gap-3">
                  <CheckCircle2 className={`h-5 w-5 text-${program.color}-600 flex-shrink-0 mt-0.5`} />
                  <span className="text-zinc-700 leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-800">Designed for</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 leading-tight">
              Who benefits most
            </h2>
            <ul className="mt-8 space-y-3">
              {program.audiences.map((a, i) => (
                <li key={`aud-${i}-${String(a).slice(0, 20)}`} className="flex items-start gap-3 bg-white rounded-md border border-zinc-200 p-3">
                  <Users className="h-4 w-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-zinc-700 leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* LEARNING OBJECTIVES */}
      {program.learningObjectives && (
        <section className="border-b border-zinc-200 bg-white" data-testid="program-learning-objectives">
          <div className="max-w-5xl mx-auto px-6 py-20">
            <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-3">Learning objectives</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              By the end of this program, you will be able to:
            </h2>
            <div className="mt-10 grid md:grid-cols-2 gap-4">
              {program.learningObjectives.map((o, i) => (
                <div key={`lo-${i}-${String(o).slice(0, 20)}`} className="flex items-start gap-3 border-l-2 border-red-600 pl-4 py-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-red-800 flex-shrink-0 mt-0.5">
                    LO {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-sm text-zinc-700 leading-relaxed">{o}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CURRICULUM (module list) */}
      {program.curriculum && program.curriculum.length > 0 && (
        <section className="border-b border-zinc-200 bg-zinc-50" data-testid="program-curriculum">
          <div className="max-w-5xl mx-auto px-6 py-20">
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-red-800">Course modules</div>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 leading-tight">
                  Module-by-module breakdown
                </h2>
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                {program.curriculum.length} modules · {program.durationLabel}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
              {program.curriculum.map((m) => (
                <div key={m.n} className="grid grid-cols-12 gap-4 p-5 hover:bg-zinc-50 transition" data-testid={`module-${m.n}`}>
                  <div className="col-span-1">
                    <div className={`inline-flex items-center justify-center w-9 h-9 rounded-md bg-${program.color}-50 text-${program.color}-700 font-mono font-bold text-sm`}>
                      {String(m.n).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="col-span-11 min-w-0">
                    <div className="font-display font-bold text-lg leading-tight">{m.title}</div>
                    <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{m.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PREREQUISITES · ASSESSMENT · STANDARDS */}
      {(program.prerequisites || program.assessment || program.standards) && (
        <section className="border-b border-zinc-200 bg-white" data-testid="program-meta">
          <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-8">
            {program.prerequisites && (
              <div>
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-red-50 mb-4">
                  <GraduationCap className="h-5 w-5 text-red-800" />
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Prerequisites</div>
                <h3 className="font-display font-bold text-lg mt-1">Before you enroll</h3>
                <p className="text-sm text-zinc-700 mt-3 leading-relaxed">{program.prerequisites}</p>
              </div>
            )}
            {program.assessment && (
              <div>
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-red-50 mb-4">
                  <FileCheck2 className="h-5 w-5 text-red-800" />
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Assessment</div>
                <h3 className="font-display font-bold text-lg mt-1">How you&rsquo;re evaluated</h3>
                <p className="text-sm text-zinc-700 mt-3 leading-relaxed">{program.assessment}</p>
              </div>
            )}
            {program.standards && (
              <div>
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-red-50 mb-4">
                  <Target className="h-5 w-5 text-red-800" />
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Aligned with</div>
                <h3 className="font-display font-bold text-lg mt-1">Standards &amp; frameworks</h3>
                <ul className="mt-3 space-y-1.5">
                  {program.standards.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-sm text-zinc-700">
                      <ChevronRight className="h-3.5 w-3.5 text-red-600 flex-shrink-0 mt-1" />
                      <span className="leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      {isLive ? (
        <section className="bg-gradient-to-r from-red-800 to-red-900 text-white">
          <div className="max-w-5xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-red-200">Ready to certify?</div>
              <h3 className="font-display text-3xl font-bold mt-2">Enroll individually or for your entire team.</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to={ctaHref} className="bg-white text-red-800 hover:bg-red-50 h-11 px-6 inline-flex items-center gap-2 rounded-md text-sm font-medium">
                {user ? "Go to my dashboard" : "Get certified — $30"} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/corporate" className="border-2 border-white/40 hover:bg-white/10 text-white h-11 px-6 inline-flex items-center gap-2 rounded-md text-sm font-medium">
                <Building2 className="h-4 w-4" /> Certify a team
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-zinc-900 text-white">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <Sparkles className="h-8 w-8 text-red-400 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-3xl md:text-4xl font-bold">This program is launching soon.</h3>
            <p className="text-zinc-400 mt-3 max-w-xl mx-auto">
              Join the waitlist to be notified the moment enrollment opens, and to help shape the curriculum.
            </p>
            <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white h-11 px-6 rounded-md text-sm font-medium">
              Join the waitlist <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* RELATED */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <h3 className="font-display text-2xl font-bold mb-8">Other programs</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((r) => {
              const RIcon = ICONS[r.icon] || ShieldCheck;
              return (
                <Link key={r.slug} to={`/programs/${r.slug}`} className="border border-zinc-200 rounded-lg p-5 hover:border-red-600 transition group">
                  <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-${r.color}-50 mb-3`}>
                    <RIcon className={`h-5 w-5 text-${r.color}-600`} />
                  </div>
                  <div className="font-display font-bold text-lg leading-tight">{r.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{r.tagline}</div>
                  <div className="mt-3 text-xs text-red-700 group-hover:text-red-900 font-mono uppercase tracking-widest">
                    Learn more →
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </DoospLayout>
  );
}
